import { useQuery } from '@tanstack/react-query';
import { Frequency } from '../../components/manageMentorship/types/frequency';
import { RepeatMonthlyStrategy } from '../../components/manageMentorship/types/repeatMonthlyStrategy';
import { RepeatStrategy } from '../../components/manageMentorship/types/repeatStrategy';
import { Weekday } from '../../components/manageMentorship/types/weekday';
import { userProfileQueries } from '../../queries';
const strategyByFrequencyMap = {
    [Frequency.Daily]: RepeatStrategy.Daily,
    [Frequency.WeeklyOnDay]: RepeatStrategy.Weekly,
    [Frequency.MonthlyOnFirstWeekday]: RepeatStrategy.Monthly,
    [Frequency.MonthlyOnSecondWeekday]: RepeatStrategy.Monthly,
    [Frequency.MonthlyOnThirdWeekday]: RepeatStrategy.Monthly,
    [Frequency.MonthlyOnFourthWeekday]: RepeatStrategy.Monthly,
    [Frequency.MonthlyOnLastWeekday]: RepeatStrategy.Monthly,
    [Frequency.Weekday]: RepeatStrategy.Weekly
};
function getWeekday(date) {
    return new Intl.DateTimeFormat('EN', { weekday: 'long' }).format(date);
}
function getStrategy(formValues) {
    const { isRecurrent, frequency } = formValues.recurringSession;
    if (!isRecurrent) {
        return RepeatStrategy.NoRepetition;
    }
    if (frequency !== Frequency.Custom) {
        return strategyByFrequencyMap[frequency];
    }
    return formValues.recurringSession.repeat.strategy;
}
function getRepeatWeekConfig(formValues) {
    const frequency = formValues.recurringSession.frequency;
    const weeklyStrategy = formValues.recurringSession.repeat?.weeklyStrategy;
    const weekday = getWeekday(formValues.date).toLowerCase();
    if (frequency == Frequency.Custom) {
        return weeklyStrategy.reduce((obj, weekDay) => ({
            ...obj,
            [weekDay]: true
        }), {});
    }
    if (frequency == Frequency.Weekday) {
        return {
            [Weekday.Monday]: true,
            [Weekday.Tuesday]: true,
            [Weekday.Wednesday]: true,
            [Weekday.Thursday]: true,
            [Weekday.Friday]: true,
            [Weekday.Saturday]: true,
            [Weekday.Sunday]: true
        };
    }
    return {
        [weekday]: true
    };
}
function getRepeatMonthConfig(formValues) {
    const frequency = formValues.recurringSession.frequency;
    const monthlyStrategy = formValues.recurringSession.repeat?.monthlyStrategy;
    const weekday = getWeekday(formValues.date).toUpperCase();
    if (frequency == Frequency.Custom) {
        if (monthlyStrategy == RepeatMonthlyStrategy.OnDay) {
            const day = formValues.date.getDate();
            return { day };
        }
        return {
            dayOfTheWeek: weekday,
            dayOfTheWeekStrategy: monthlyStrategy
        };
    }
    return {
        dayOfTheWeek: weekday,
        dayOfTheWeekStrategy: frequency
    };
}
export function useMentorshipPayload() {
    const userProfile = useQuery({
        ...userProfileQueries.getUser
    });
    function mapMentorshipFormValuesToPayload(formValues) {
        const isRecurrent = formValues.recurringSession.isRecurrent;
        const year = formValues.date.getFullYear();
        const month = formValues.date.getMonth() + 1;
        const day = formValues.date.getDate();
        const startAt = new Date(`${year}-${month}-${day} ${formValues.startTime}`);
        const endAt = new Date(`${year}-${month}-${day} ${formValues.endTime}`);
        const strategy = getStrategy(formValues);
        const repeatWeekConfig = getRepeatWeekConfig(formValues);
        const repeatMonthConfig = getRepeatMonthConfig(formValues);
        return {
            extra: true,
            paidModule: false,
            publicModule: false,
            name: formValues.name,
            classes: formValues.classes,
            coverImageId: formValues.cover?.id ?? null,
            imageId: formValues.imageId,
            mediaId: formValues.mediaId,
            mentorshipConfig: {
                type: 'GROUP_EVENT',
                link: formValues.link,
                repeatConfig: {
                    startAt: startAt.toISOString(),
                    endAt: endAt.toISOString(),
                    strategy,
                    timeZone: userProfile.data?.timeZone,
                    ...(isRecurrent && {
                        every: formValues.recurringSession.repeat.every,
                        recurrenceEndAt: formValues.recurringSession.endAt?.toISOString(),
                        recurrenceDescription: formValues.recurringSession.frequency,
                        ...(strategy == RepeatStrategy.Weekly && { repeatWeekConfig }),
                        ...(strategy == RepeatStrategy.Monthly && { repeatMonthConfig })
                    })
                }
            }
        };
    }
    return { mapMentorshipFormValuesToPayload };
}
